import React, { useEffect, useMemo, useState } from "react";
import { hasQuotedReply, stripQuotedReply, buildSnippet } from "rebase-leadgen-config";
import {
    Button,
    Card,
    Chip,
    cls,
    defaultBorderMixin,
    iconSize,
    LucideIconByName,
    Typography
} from "@rebasepro/ui";
import { downloadAttachment, fullStamp, readerDocument, sinceLabel, type MailboxSummary, type Message } from "./shared";

/**
 * A conversation, read downwards.
 *
 * Oldest first, newest open. Everything before the newest message is a single
 * line you can open; a long thread folds its middle into one "N earlier
 * messages" pill, because the question on opening a conversation is almost
 * always "what did they just say", and the answer should not be under eight
 * cards of history.
 *
 * Ours are indented and tinted; theirs sit flush left — the one cue that says
 * which way a message went before any label is read.
 */

const HEAD = 1;
const TAIL = 2;

const STATUS_LABEL: Record<string, string> = { received: "Received", sent: "Sent", failed: "Did not send" };

function senderOf(message: Message, mailboxes: readonly MailboxSummary[]): string {
    if (message.direction === "inbound") return message.from_name || message.from_email || "Someone";
    const mailbox = mailboxes.find(m => m.id === message.mailboxId);
    return mailbox?.fromName || mailbox?.name || message.from_name || message.from_email || "Us";
}

export function EmailThreadView({ messages, mailboxes }: { messages: Message[]; mailboxes: readonly MailboxSummary[] }) {
    const ordered = useMemo(
        () => [...messages].sort((a, b) => String(a.sent_at ?? "").localeCompare(String(b.sent_at ?? ""))),
        [messages]
    );
    const newestId = ordered.at(-1)?.id ?? null;
    const [opened, setOpened] = useState<string[]>([]);
    const [stackOpen, setStackOpen] = useState(false);
    // A new message arriving closes what was opened: it is the one to read.
    useEffect(() => { setOpened([]); setStackOpen(false); }, [newestId]);

    if (ordered.length === 0) {
        return <Typography variant="body2" color="secondary">This conversation has no messages yet.</Typography>;
    }

    const stackable = ordered.length - HEAD - TAIL;
    const hidden = stackable >= 2 && !stackOpen
        ? new Set(ordered.slice(HEAD, HEAD + stackable).map(m => m.id))
        : new Set<string>();

    return (
        <div className="flex flex-col gap-2">
            {ordered.map((message, index) => {
                if (hidden.has(message.id)) {
                    return message.id === ordered[HEAD].id ? (
                        <button
                            key="stack"
                            type="button"
                            onClick={() => setStackOpen(true)}
                            className={cls("self-start px-3 py-1 rounded-full border text-sm hover:bg-surface-accent-100 dark:hover:bg-surface-accent-800", defaultBorderMixin)}
                        >
                            <Typography variant="body2" color="secondary" component="span">··· {hidden.size} earlier messages</Typography>
                        </button>
                    ) : null;
                }
                const open = index === ordered.length - 1 || opened.includes(message.id);
                return open
                    ? <EmailMessageCard key={message.id} message={message} mailboxes={mailboxes} />
                    : <CollapsedMessage key={message.id} message={message} mailboxes={mailboxes} onOpen={() => setOpened(ids => [...ids, message.id])} />;
            })}
        </div>
    );
}

function CollapsedMessage({ message, mailboxes, onOpen }: { message: Message; mailboxes: readonly MailboxSummary[]; onOpen: () => void }) {
    const preview = message.body_snippet || buildSnippet(message.body_text ?? "");
    return (
        <button
            type="button"
            onClick={onOpen}
            className={cls(
                "w-full text-left flex items-baseline gap-3 px-3 py-2 rounded-lg border hover:bg-surface-accent-50 dark:hover:bg-surface-accent-900",
                defaultBorderMixin,
                message.direction === "outbound" && "ml-6 w-[calc(100%-1.5rem)]"
            )}
        >
            <Typography variant="body2" component="span" className="font-medium truncate max-w-[40%] shrink-0">
                {senderOf(message, mailboxes)}
            </Typography>
            {message.status === "failed" && <Typography variant="caption" color="error" component="span" className="shrink-0">did not send</Typography>}
            <Typography variant="body2" color="secondary" component="span" className="truncate grow">{preview}</Typography>
            <Typography variant="caption" color="secondary" component="span" className="shrink-0">{sinceLabel(message.sent_at)}</Typography>
        </button>
    );
}

export function EmailMessageCard({ message, mailboxes }: { message: Message; mailboxes: readonly MailboxSummary[] }) {
    const inbound = message.direction === "inbound";
    const status = message.status ?? (inbound ? "received" : "sent");
    const text = (message.body_text ?? "").trim();
    const quoted = hasQuotedReply(text);
    const [showQuoted, setShowQuoted] = useState(false);
    const [formatted, setFormatted] = useState(false);
    const [fetching, setFetching] = useState<string | undefined>();
    const [attachmentError, setAttachmentError] = useState<string | undefined>();
    const shown = quoted && !showQuoted ? stripQuotedReply(text) : text;
    const attachments = (message.attachment_names ?? []).filter(Boolean);

    const open = async (name: string) => {
        setFetching(name);
        setAttachmentError(undefined);
        try {
            await downloadAttachment(message.id, name);
        } catch (err) {
            setAttachmentError(err instanceof Error ? err.message : String(err));
        } finally {
            setFetching(undefined);
        }
    };

    return (
        <Card className={cls(
            "p-3 flex flex-col gap-2",
            defaultBorderMixin,
            !inbound && "ml-6 bg-surface-accent-50 dark:bg-surface-accent-900"
        )}>
            <div className="flex items-start gap-2">
                <div className="grow min-w-0">
                    <Typography variant="body2" className="font-medium truncate">
                        {senderOf(message, mailboxes)}
                        {inbound && message.from_name && message.from_email && (
                            <span className="ml-2 text-xs font-normal font-mono text-text-secondary dark:text-text-secondary-dark">
                                {message.from_email}
                            </span>
                        )}
                    </Typography>
                    <Typography variant="caption" color="secondary" className="block truncate">
                        {fullStamp(message.sent_at)}
                        {(message.to_emails ?? []).length > 0 && ` · to ${(message.to_emails ?? []).join(", ")}`}
                        {(message.cc_emails ?? []).length > 0 && ` · cc ${(message.cc_emails ?? []).join(", ")}`}
                        {!inbound && message.actor && ` · sent by ${message.actor}`}
                    </Typography>
                </div>
                <Chip size="small" colorScheme={status === "failed" ? "redLighter" : inbound ? "greenLighter" : "blueLighter"}>
                    {STATUS_LABEL[status] ?? status}
                </Chip>
            </div>

            {status === "failed" && message.error && (
                <Typography variant="body2" color="error">This one did not send: {message.error}</Typography>
            )}

            {shown && (
                <Typography variant="body1" className="whitespace-pre-wrap break-words">{shown}</Typography>
            )}

            {formatted && message.body_html && (
                <iframe
                    sandbox=""
                    referrerPolicy="no-referrer"
                    title="The message with its formatting"
                    srcDoc={readerDocument(message.body_html)}
                    className={cls("w-full rounded-md border bg-white", defaultBorderMixin)}
                    style={{ height: 480 }}
                />
            )}

            {(quoted || message.body_html || attachments.length > 0) && (
                <div className="flex items-center gap-1 flex-wrap -ml-2">
                    {quoted && (
                        <Button size="small" variant="text" onClick={() => setShowQuoted(v => !v)}>
                            {showQuoted ? "Hide the quoted part" : "Show the whole message"}
                        </Button>
                    )}
                    {message.body_html && (
                        <Button size="small" variant="text" onClick={() => setFormatted(v => !v)}>
                            {formatted ? "Hide the formatting" : "View with formatting"}
                        </Button>
                    )}
                    {attachments.map(name => (
                        <Button
                            key={name}
                            size="small"
                            variant="text"
                            disabled={fetching !== undefined || !message.imap_uid}
                            onClick={() => void open(name)}
                        >
                            <LucideIconByName name="Paperclip" size={iconSize.smallest} />
                            {fetching === name ? `Opening ${name}…` : name}
                        </Button>
                    ))}
                </div>
            )}
            {attachmentError && <Typography variant="caption" color="error">{attachmentError}</Typography>}
        </Card>
    );
}
